/**
 * Unit conversion + display formatting.
 *
 * Storage is canonical metric everywhere (weight in kg, distance in metres,
 * water in millilitres, height in cm). The single user prefers IMPERIAL for
 * display, so this module is the one place metric values are turned into the
 * pounds / miles / fluid-ounces / feet+inches shown in the UI. Ingestion, the
 * API, and the database are untouched — only the display + input layer reads
 * these helpers.
 *
 * Unit preference lives in the `units` setting ('imperial' | 'metric'); when it
 * is unset we DEFAULT to imperial.
 */

import { metricMeta } from '../metrics.js';

export type UnitPref = 'imperial' | 'metric';

// Exact conversion factors.
export const KG_PER_LB = 0.45359237;
export const M_PER_MI = 1609.344;
export const ML_PER_FLOZ = 29.5735295625; // US fluid ounce
export const CM_PER_IN = 2.54;

export function kgToLb(kg: number): number {
  return kg / KG_PER_LB;
}
export function lbToKg(lb: number): number {
  return lb * KG_PER_LB;
}
export function mToMi(m: number): number {
  return m / M_PER_MI;
}
export function miToM(mi: number): number {
  return mi * M_PER_MI;
}
export function kmToMi(km: number): number {
  return (km * 1000) / M_PER_MI;
}
export function miToKm(mi: number): number {
  return (mi * M_PER_MI) / 1000;
}
export function mlToFlOz(ml: number): number {
  return ml / ML_PER_FLOZ;
}
export function flOzToMl(flOz: number): number {
  return flOz * ML_PER_FLOZ;
}

export interface FtIn {
  ft: number;
  in: number;
}

/** cm → whole feet + rounded whole inches (inches roll over to a foot at 12). */
export function cmToFtIn(cm: number): FtIn {
  const totalIn = cm / CM_PER_IN;
  let ft = Math.floor(totalIn / 12);
  let inch = Math.round(totalIn - ft * 12);
  if (inch === 12) {
    ft += 1;
    inch = 0;
  }
  return { ft, in: inch };
}

/** feet + inches → cm. */
export function ftInToCm(ft: number, inch: number): number {
  return (ft * 12 + inch) * CM_PER_IN;
}

function roundTo(v: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round(v * f) / f;
}

interface ImperialSpec {
  unit: string;
  convert: (v: number) => number;
  digits: number;
}

/**
 * The metric_types that get converted for imperial display. Everything else
 * (heart rate, %, kcal, mg, lab values, …) is unit-agnostic and passes through
 * with its canonical unit untouched.
 */
const IMPERIAL: Record<string, ImperialSpec> = {
  weight_kg: { unit: 'lb', convert: kgToLb, digits: 1 },
  lean_body_mass_kg: { unit: 'lb', convert: kgToLb, digits: 1 },
  distance_m: { unit: 'mi', convert: mToMi, digits: 2 },
  water_ml: { unit: 'fl oz', convert: mlToFlOz, digits: 1 },
};

export interface FormattedMetric {
  value: number;
  unit: string;
}

/**
 * Convert a canonical metric value into the display value + unit for the given
 * preference. Imperial converts the four convertible types; metric (and every
 * other type) returns the value unchanged with the canonical unit.
 */
export function formatMetric(
  type: string,
  value: number,
  pref: UnitPref = 'imperial',
): FormattedMetric {
  const spec = IMPERIAL[type];
  if (pref === 'imperial' && spec && Number.isFinite(value)) {
    return { value: roundTo(spec.convert(value), spec.digits), unit: spec.unit };
  }
  return { value, unit: metricMeta(type).unit };
}

/** Display unit for a metric type + preference, without needing a value. */
export function displayUnit(type: string, pref: UnitPref = 'imperial'): string {
  const spec = IMPERIAL[type];
  return pref === 'imperial' && spec ? spec.unit : metricMeta(type).unit;
}

/** Parse the stored `units` setting into a preference (imperial default). */
export function parseUnitPref(stored: unknown): UnitPref {
  return stored === 'metric' ? 'metric' : 'imperial';
}
